import { useTranslation } from 'react-i18next';
import {
  Alert,
  CircularProgress,
  Icons,
  MenuItem,
  Stack,
  SUPPORTED_TIMEZONES,
  TextField,
  useSnackbar,
} from '@munifocus/ui';
import {
  DIGEST_FREQUENCY_VALUES,
  DIGEST_LANGUAGE_VALUES,
  type UpdateTeamworkPreferencesDto,
} from '@munifocus/contracts-gov';
import { useTeamworkPreferences, useUpdateTeamworkPreferences } from '../../lib/teamwork-api';
import { SettingsSection as Section } from './SettingsSection';

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];

/** `07:00` for hour 7, in the 24-hour form both languages use in Canada's public service. */
export function hourLabel(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/**
 * The officer's own work summary: deadlines today and this week, overdue
 * items, new assignments and mentions, mailed daily or weekly at the hour and
 * in the language they choose. Saves on every change, like the notification
 * switches above it.
 */
export function WorkSummarySection() {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const prefs = useTeamworkPreferences();
  const update = useUpdateTeamworkPreferences();
  const data = prefs.data;

  const save = (patch: UpdateTeamworkPreferencesDto) =>
    update.mutate(patch, {
      onSuccess: () => snackbar.success(t('gov.teamwork.digest.saved')),
      onError: () => snackbar.error(t('gov.teamwork.digest.error')),
    });

  return (
    <Section
      icon={Icons.Calendar}
      title={t('gov.teamwork.digest.title')}
      description={t('gov.teamwork.digest.description')}
    >
      {prefs.isError && <Alert severity="error">{t('gov.teamwork.digest.loadError')}</Alert>}
      {prefs.isLoading && (
        <CircularProgress size={20} aria-label={t('gov.teamwork.digest.loading')} />
      )}
      {data && (
        <Stack spacing={2}>
          {!data.outboundEnabled && (
            <Alert severity="info">{t('gov.teamwork.digest.outboundOff')}</Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              fullWidth
              label={t('gov.teamwork.digest.frequency')}
              value={data.digestFrequency}
              disabled={update.isPending}
              onChange={(e) =>
                save({
                  digestFrequency: e.target
                    .value as UpdateTeamworkPreferencesDto['digestFrequency'],
                })
              }
              slotProps={{ htmlInput: { 'data-testid': 'digest-frequency' } }}
            >
              {DIGEST_FREQUENCY_VALUES.map((f) => (
                <MenuItem key={f} value={f}>
                  {t(`gov.teamwork.digest.frequencies.${f}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label={t('gov.teamwork.digest.hour')}
              value={data.digestHour}
              disabled={update.isPending || data.digestFrequency === 'off'}
              onChange={(e) => save({ digestHour: Number(e.target.value) })}
            >
              {HOURS.map((h) => (
                <MenuItem key={h} value={h}>
                  {hourLabel(h)}
                </MenuItem>
              ))}
            </TextField>
            {data.digestFrequency === 'weekly' && (
              <TextField
                select
                fullWidth
                label={t('gov.teamwork.digest.weekday')}
                value={data.digestWeekday}
                disabled={update.isPending}
                onChange={(e) => save({ digestWeekday: Number(e.target.value) })}
              >
                {WEEKDAYS.map((d) => (
                  <MenuItem key={d} value={d}>
                    {t(`gov.teamwork.digest.weekdays.${d}`)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              fullWidth
              label={t('gov.teamwork.digest.language')}
              value={data.digestLanguage}
              disabled={update.isPending || data.digestFrequency === 'off'}
              onChange={(e) =>
                save({
                  digestLanguage: e.target.value as UpdateTeamworkPreferencesDto['digestLanguage'],
                })
              }
            >
              {DIGEST_LANGUAGE_VALUES.map((l) => (
                <MenuItem key={l} value={l}>
                  {t(`gov.teamwork.digest.languages.${l}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label={t('common.notifications.settings.timezoneLabel')}
              value={data.timezone}
              disabled={update.isPending || data.digestFrequency === 'off'}
              onChange={(e) => save({ timezone: e.target.value })}
            >
              {[...new Set([...SUPPORTED_TIMEZONES, data.timezone])].map((z) => (
                <MenuItem key={z} value={z}>
                  {z}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      )}
    </Section>
  );
}
